import { FC } from 'react'
import { Accordion, Button, Col, Form, Row } from 'react-bootstrap'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
    faPalette,
    faRotateLeft,
} from '@fortawesome/free-solid-svg-icons'
import { useAppSelector } from '../../../../../store/hooks'
import TextField from '../../../../common/forms/fields/TextField'
import TextAreaField from '../../../../common/forms/fields/TextAreaField'
import SelectField from '../../../../common/forms/fields/SelectField'
import ImageField from '../../../../common/forms/fields/ImageField'
import slugify from 'react-slugify'
import {
    getDefaultPreviewTheme,
    removeSettingsValue,
    setSettingsValue,
} from '../../../../common/utility/helpers'
import { PropertiesPanelProps } from '../types'

const PropertiesPanel: FC<PropertiesPanelProps> = ({
    isOpen,
    page,
    formikProps,
    headerSizes,
    previewTheme,
    setPreviewTheme,
    previewSrc,
    setPreviewSrc,
    allowRemoveImage,
    setAllowRemoveImage,
    setPage,
}) => {
    const theme = useAppSelector((state) => state.theme.theme)

    const defaultPreviewTheme = getDefaultPreviewTheme()

    const handleChangePreviewTheme = (
        e: React.ChangeEvent<HTMLInputElement>,
        type: string
    ) => {
        setPreviewTheme((prevState) => {
            const pt = prevState
                ? {
                      ...prevState,
                      colors: {
                          ...prevState.colors,
                          [type]: {
                              ...prevState.colors[type],
                              hex: e.target.value,
                          },
                      },
                  }
                : null
            setSettingsValue('previewTheme', pt)
            return pt
        })
    }

    return (
        <div
            className={`sfy-properties-panel ${
                isOpen ? 'sfy-properties-panel--open' : ''
            } ${theme === 'dark' ? 'sfy-properties-panel--dark' : ''}`}
        >
            <div className="sfy-properties-panel__inner">
                    <>
                        <div className="sfy-properties-panel__header">
                            <div className="sfy-properties-panel__title">
                                <FontAwesomeIcon
                                    icon={faPalette}
                                    className="me-2"
                                />
                                <span>Page Properties</span>
                            </div>
                        </div>
                        <div className="sfy-properties-panel__body">
                            <Accordion
                                defaultActiveKey={['0']}
                                alwaysOpen
                                flush
                            >
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>General</Accordion.Header>
                                    <Accordion.Body>
                                        <TextField
                                            id="builderFormPageTitle"
                                            name="page_title"
                                            label="Page Title"
                                            placeholder="Enter page title"
                                            formikProps={formikProps}
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                setPage((prevState) => ({
                                                    ...prevState,
                                                    page_title: e.target.value,
                                                    code: slugify(
                                                        formikProps.values
                                                            .code ||
                                                            formikProps.values
                                                                .page_title
                                                    ),
                                                }))
                                            }}
                                        />
                                        <TextField
                                            id="builderFormPageUrl"
                                            name="code"
                                            label="Page URL"
                                            placeholder="Enter page URL"
                                            formikProps={formikProps}
                                            onBlur={(e: React.FocusEvent<HTMLInputElement>) => {
                                                formikProps.setFieldValue(
                                                    'code',
                                                    e.target.value
                                                        ?.toString()
                                                        .toLowerCase()
                                                )
                                            }}
                                            required
                                        />
                                        <TextField
                                            id="builderFormPageName"
                                            name="name"
                                            label="Internal Name"
                                            placeholder="Enter internal name"
                                            formikProps={formikProps}
                                            required
                                        />
                                        <SelectField
                                            id="builderFormHeaderSize"
                                            name="header_size_id"
                                            label="Header Size"
                                            formikProps={formikProps}
                                            choices={headerSizes}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="1">
                                    <Accordion.Header>
                                        Theme Colors
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <Row>
                                            <Col xs={5}>
                                                <Form.Group className="sfy-field mb-3">
                                                    <Form.Label>
                                                        Primary
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="color"
                                                        value={
                                                            previewTheme
                                                                ?.colors
                                                                ?.primary
                                                                ?.hex ||
                                                            defaultPreviewTheme
                                                                .colors.primary
                                                                .hex
                                                        }
                                                        onChange={(e) =>
                                                            handleChangePreviewTheme(
                                                                e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                                'primary'
                                                            )
                                                        }
                                                        className="w-100"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={5}>
                                                <Form.Group className="sfy-field mb-3">
                                                    <Form.Label>
                                                        Secondary
                                                    </Form.Label>
                                                    <Form.Control
                                                        type="color"
                                                        value={
                                                            previewTheme
                                                                ?.colors
                                                                ?.secondary
                                                                ?.hex ||
                                                            defaultPreviewTheme
                                                                .colors
                                                                .secondary.hex
                                                        }
                                                        onChange={(e) =>
                                                            handleChangePreviewTheme(
                                                                e as unknown as React.ChangeEvent<HTMLInputElement>,
                                                                'secondary'
                                                            )
                                                        }
                                                        className="w-100"
                                                    />
                                                </Form.Group>
                                            </Col>
                                            <Col xs={2} className="d-flex align-items-end pb-3">
                                                <Button
                                                    variant="outline-secondary"
                                                    size="sm"
                                                    className="w-100"
                                                    title="Reset theme"
                                                    onClick={() => {
                                                        removeSettingsValue(
                                                            'previewTheme'
                                                        )
                                                        setPreviewTheme(null)
                                                    }}
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faRotateLeft}
                                                    />
                                                </Button>
                                            </Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="2">
                                    <Accordion.Header>
                                        Header Image
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <ImageField
                                            id="builderPageImageFile"
                                            name="header_image"
                                            label="Image"
                                            formikProps={formikProps}
                                            imageId={page.header_image_id}
                                            alt={`${page.name} image`}
                                            allowRemoveImage={allowRemoveImage}
                                            setAllowRemoveImage={
                                                setAllowRemoveImage
                                            }
                                            previewSrc={previewSrc}
                                            setPreviewSrc={setPreviewSrc}
                                            updateRecord={setPage}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>

                                <Accordion.Item eventKey="3">
                                    <Accordion.Header>
                                        Meta Data
                                    </Accordion.Header>
                                    <Accordion.Body>
                                        <TextAreaField
                                            id="builderFormMetaDescription"
                                            name="meta_description"
                                            label="Description"
                                            formikProps={formikProps}
                                        />
                                        <TextAreaField
                                            id="builderFormMetaKeywords"
                                            name="meta_keywords"
                                            label="Keywords"
                                            formikProps={formikProps}
                                        />
                                    </Accordion.Body>
                                </Accordion.Item>

                            </Accordion>
                        </div>
                    </>
            </div>
        </div>
    )
}

export default PropertiesPanel
